import React, { useState, useEffect, useCallback } from 'react';
import { 
  Building2, Users, FileText, AlertTriangle, TrendingUp,
  CheckCircle, Clock, BarChart3, Loader2, Activity, Shield,
  MailX, MailWarning, ShieldCheck, ArrowRight, RotateCcw, Radio,
  Webhook, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import ReopenStatsCard from './ReopenStatsCard';

const HUB_STATUS_STYLES = {
  green: { dot: 'bg-emerald-500', ring: 'ring-emerald-200', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  amber: { dot: 'bg-amber-500', ring: 'ring-amber-200', pill: 'bg-amber-50 text-amber-800 border-amber-200' },
  red:   { dot: 'bg-red-500',    ring: 'ring-red-200',    pill: 'bg-red-50 text-red-700 border-red-200' },
  grey:  { dot: 'bg-slate-400',  ring: 'ring-slate-200',  pill: 'bg-slate-50 text-slate-600 border-slate-200' },
};

const HubStatusBadge = ({ hub, onClick }) => {
  if (!hub) return null;
  const s = HUB_STATUS_STYLES[hub.status] || HUB_STATUS_STYLES.grey;
  const lastLine = hub.last_success?.created_at
    ? `Última entrega: ${new Date(hub.last_success.created_at).toLocaleString('pt-PT')}`
    : 'Sem entregas registadas';
  const failLine = hub.last_failure?.created_at
    ? `Última falha: ${new Date(hub.last_failure.created_at).toLocaleString('pt-PT')}${hub.last_failure.status_code ? ` (HTTP ${hub.last_failure.status_code})` : ''}`
    : null;

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors hover:brightness-95 ${s.pill}`}
            data-testid="hub-status-badge"
          >
            <span className="relative flex h-2.5 w-2.5">
              {hub.status === 'green' && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
              )}
              <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ring-2 ${s.dot} ${s.ring}`} />
            </span>
            <Radio className="w-3.5 h-3.5" />
            {hub.label}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="max-w-xs text-xs leading-relaxed">
          <p className="font-semibold text-sm mb-1">{hub.label}</p>
          <p className="opacity-90">{hub.detail}</p>
          <div className="mt-2 pt-2 border-t border-white/20 space-y-0.5 opacity-80">
            <p>Webhook: {hub.webhook_enabled ? 'activo' : 'desactivado'}{hub.webhook_has_secret ? ' · assinado' : ''}</p>
            <p>Chave M2M: {hub.provisioning_key_set ? 'configurada' : '❌ não configurada'}</p>
            <p>{lastLine}</p>
            {failLine && <p className="text-red-300">{failLine}</p>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [hub, setHub] = useState(null);
  const [backlog, setBacklog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resetting, setResetting] = useState('');
  const [redeliveringAll, setRedeliveringAll] = useState(false);
  const navigate = useNavigate();

  const fetchStats = useCallback(async () => {
    try {
      const [dash, hlth, hubRes, backlogRes] = await Promise.all([
        api.get('/super-admin/dashboard'),
        api.get('/super-admin/email-health').catch(() => ({ data: null })),
        api.get('/super-admin/hub-status').catch(() => ({ data: null })),
        api.get('/super-admin/webhook-logs/backlog-alert').catch(() => ({ data: null })),
      ]);
      setStats(dash.data);
      setHealth(hlth.data);
      setHub(hubRes.data);
      setBacklog(backlogRes.data);
    } catch (err) {
      setError('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleRedeliverAllBacklog = async () => {
    setRedeliveringAll(true);
    try {
      const res = await api.post('/super-admin/webhook-logs/redeliver-all-failed');
      toast.success(`Reentregues: ${res.data.delivered} · Ainda a falhar: ${res.data.still_failing}`);
      await fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na reentrega em lote');
    } finally {
      setRedeliveringAll(false);
    }
  };

  const resetHealth = async (tenantId, allChannels = false) => {
    const key = allChannels ? 'all' : (tenantId || 'platform');
    setResetting(key);
    try {
      await api.post('/super-admin/email-health/reset', {
        tenant_id: tenantId || null,
        reset_all: allChannels,
      });
      toast.success(allChannels ? 'Todos os alertas foram repostos' : 'Alerta reposto para este canal');
      await fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao repor alertas de saúde');
    } finally {
      setResetting('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-700" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Clientes',
      value: stats?.total_tenants || 0,
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      subtitle: `${stats?.active_tenants || 0} ativos`,
      link: '/super-admin/tenants'
    },
    {
      title: 'Total Utilizadores',
      value: stats?.total_users || 0,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      subtitle: 'Em todos os tenants',
      link: '/super-admin/tenants'
    },
    {
      title: 'Total Denúncias',
      value: stats?.total_reports || 0,
      icon: FileText,
      color: 'text-indigo-700',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      subtitle: `${stats?.reports_this_month || 0} este mês`,
      link: '/super-admin/provisionamento'
    },
    {
      title: 'Denúncias Pendentes',
      value: stats?.pending_reports || 0,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
      subtitle: 'A aguardar tratamento',
      link: '/super-admin/tenants'
    },
    {
      title: 'Em Atraso',
      value: stats?.overdue_reports || 0,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      subtitle: 'Prazos ultrapassados',
      link: '/super-admin/tenants'
    },
    {
      title: 'Conflitos Detetados',
      value: stats?.conflict_alerts || 0,
      icon: Shield,
      color: 'text-purple-600',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      subtitle: 'Este mês',
      link: '/super-admin/auditoria'
    }
  ];

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-indigo-700" />
            Dashboard Super Admin
          </h1>
          <p className="text-slate-500 mt-1">
            Visão global da plataforma CogniEtica
          </p>
        </div>
        <HubStatusBadge hub={hub} onClick={() => navigate('/super-admin/provisionamento?tab=webhook')} />
      </div>

      {/* Webhook backlog alert (severity: amber = struggling, red = exhausted) */}
      {backlog && backlog.total > 0 && (
        <Card
          className={`border-2 ${backlog.severity === 'red' ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}
          data-testid="webhook-backlog-alert"
        >
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className={`flex items-center gap-2 text-lg ${backlog.severity === 'red' ? 'text-red-800' : 'text-amber-800'}`}>
                  <Webhook className="w-5 h-5" />
                  Backlog de webhooks para o CogniHub
                </CardTitle>
                <CardDescription className={backlog.severity === 'red' ? 'text-red-700' : 'text-amber-800'}>
                  {backlog.label}. O Hub anda a rejeitar sistematicamente eventos —
                  verifique se está online e se a assinatura HMAC está sincronizada.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  className={backlog.severity === 'red'
                    ? 'border-red-300 text-red-700 hover:bg-red-100'
                    : 'border-amber-400 text-amber-800 hover:bg-amber-100'}
                  onClick={handleRedeliverAllBacklog}
                  disabled={redeliveringAll}
                  data-testid="backlog-redeliver-all-btn"
                >
                  {redeliveringAll
                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    : <RefreshCw className="w-4 h-4 mr-2" />}
                  Reenviar tudo
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className={backlog.severity === 'red'
                    ? 'text-red-700 hover:bg-red-100'
                    : 'text-amber-800 hover:bg-amber-100'}
                  onClick={() => navigate('/super-admin/provisionamento?tab=webhook')}
                  data-testid="backlog-view-logs-btn"
                >
                  Ver logs <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap gap-2 text-xs">
              {backlog.struggling > 0 && (
                <Badge className="bg-amber-100 text-amber-800 border border-amber-200" data-testid="backlog-struggling-badge">
                  {backlog.struggling} com &gt;3 tentativas
                </Badge>
              )}
              {backlog.exhausted > 0 && (
                <Badge className="bg-red-100 text-red-700 border border-red-200" data-testid="backlog-exhausted-badge">
                  {backlog.exhausted} esgotado(s) — só reentrega manual
                </Badge>
              )}
            </div>
            {backlog.samples?.length > 0 && (
              <div className="mt-2 space-y-1">
                {backlog.samples.slice(0, 3).map((s) => (
                  <div key={s.log_id}
                    className="flex items-center justify-between gap-2 text-xs bg-white rounded border border-slate-200 px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${s.exhausted ? 'text-red-600' : 'text-amber-600'}`} />
                      <code className="text-slate-800 truncate">{s.event}</code>
                      <span className="text-slate-500">retry {s.retry_count}</span>
                    </div>
                    <span className="text-slate-500 shrink-0">
                      {s.status_code ? `HTTP ${s.status_code}` : (s.error || 'timeout')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delivery health alert */}
      {health && (
        health.healthy ? (
          <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4" data-testid="delivery-health-ok">
            <ShieldCheck className="w-6 h-6 text-emerald-600 shrink-0" />
            <div>
              <p className="font-semibold text-emerald-800">Entrega de emails saudável</p>
              <p className="text-sm text-emerald-700">
                Nenhum problema de entrega nos últimos {health.days} dias ({health.totals.total} emails).
              </p>
            </div>
          </div>
        ) : (
          <Card className="border-2 border-red-300 bg-red-50" data-testid="delivery-health-alert">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-red-800 flex items-center gap-2 text-lg">
                    <MailWarning className="w-5 h-5" />
                    Problemas de entrega de emails
                  </CardTitle>
                  <CardDescription className="text-red-700">
                    Estes canais têm confirmações/notificações a falhar (devolvido, spam ou sem chave) nos últimos {health.days} dias.
                    Aja antes que um denunciante fique sem resposta (Lei 93/2021).
                  </CardDescription>
                </div>
                <Button
                  variant="outline" size="sm"
                  className="border-red-300 text-red-700 hover:bg-red-100 shrink-0"
                  onClick={() => resetHealth(null, true)}
                  disabled={resetting === 'all'}
                  data-testid="reset-all-health-btn"
                >
                  {resetting === 'all' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                  Repor todos os alertas
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {health.channels.map((ch, i) => (
                <div
                  key={ch.tenant_id || `sys-${i}`}
                  className="w-full flex items-center justify-between gap-3 rounded-lg bg-white border border-red-200 px-4 py-3"
                  data-testid={`delivery-problem-${i}`}
                >
                  <button
                    onClick={() => navigate(ch.tenant_id ? `/super-admin/emails?tenant_id=${ch.tenant_id}` : '/super-admin/emails?scope=system')}
                    className="flex items-center gap-3 min-w-0 text-left flex-1 hover:opacity-80 transition-opacity"
                  >
                    <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                      <MailX className="w-4 h-4 text-red-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{ch.name}</p>
                      <p className="text-xs text-slate-500">
                        {ch.problems > 0 && <span className="text-red-600 font-medium">{ch.problems} falhas de entrega</span>}
                        {ch.problems > 0 && ch.not_configured > 0 && ' · '}
                        {ch.not_configured > 0 && <span className="text-amber-600 font-medium">{ch.not_configured} sem chave</span>}
                        {' · '}{ch.total} emails · {ch.problem_rate}% de falha
                      </p>
                    </div>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className="bg-red-100 text-red-700 border border-red-200">{ch.problems + ch.not_configured}</Badge>
                    <Button
                      variant="ghost" size="sm"
                      className="text-red-700 hover:bg-red-100 h-8"
                      onClick={() => resetHealth(ch.tenant_id, false)}
                      disabled={resetting === (ch.tenant_id || 'platform')}
                      data-testid={`reset-health-${i}`}
                    >
                      {resetting === (ch.tenant_id || 'platform') ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                    </Button>
                    <ArrowRight className="w-4 h-4 text-red-400" />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )
      )}

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} onClick={() => stat.link && navigate(stat.link)}
              className={`bg-white border ${stat.borderColor} cursor-pointer transition-transform hover:-translate-y-0.5 hover:bg-slate-100`}
              data-testid={`sa-card-${index}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500">{stat.title}</p>
                    <p className={`text-3xl font-bold ${stat.color} mt-1`}>{stat.value}</p>
                    <p className="text-xs text-slate-500 mt-1">{stat.subtitle}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tenants Overview */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Tenants */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-700" />
              Clientes Recentes
            </CardTitle>
            <CardDescription className="text-slate-500">
              Últimos tenants registados
            </CardDescription>
          </CardHeader>
          <CardContent>
            {stats?.recent_tenants?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_tenants.map((tenant, index) => (
                  <div key={tenant.id || tenant.slug || index} onClick={() => navigate('/super-admin/tenants')}
                    className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors"
                    data-testid={`recent-tenant-${index}`}>
                    <div>
                      <p className="font-medium text-slate-900">{tenant.name}</p>
                      <p className="text-sm text-slate-500">/{tenant.slug}</p>
                    </div>
                    <div className="text-right">
                      <Badge variant={tenant.is_active ? "default" : "secondary"} className={tenant.is_active ? "bg-green-100 text-green-700" : ""}>
                        {tenant.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                      <p className="text-xs text-slate-500 mt-1">{tenant.reports_count} denúncias</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-4">Nenhum tenant registado</p>
            )}
            <Button 
              variant="outline" 
              className="w-full mt-4 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              onClick={() => navigate('/super-admin/tenants')}
            >
              Ver Todos os Clientes
            </Button>
          </CardContent>
        </Card>

        {/* Activity Summary */}
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-700" />
              Resumo de Atividade
            </CardTitle>
            <CardDescription className="text-slate-500">
              Métricas dos últimos 30 dias
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-500">Taxa de Resolução</span>
                <span className="text-indigo-700 font-medium">{stats?.resolution_rate || 0}%</span>
              </div>
              <Progress value={stats?.resolution_rate || 0} className="h-2 bg-slate-700" />
            </div>
            
            <Separator className="bg-slate-700" />
            
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-500">Novas Denúncias</p>
                <p className="text-xl font-bold text-slate-900">{stats?.reports_this_month || 0}</p>
              </div>
              <div className="p-3 bg-slate-700/50 rounded-lg">
                <p className="text-xs text-slate-500">Resolvidas</p>
                <p className="text-xl font-bold text-green-600">{stats?.resolved_this_month || 0}</p>
              </div>
            </div>
            
            <Separator className="bg-slate-700" />
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Tempo Médio Resolução</span>
                <span className="text-sm text-slate-900">{stats?.avg_resolution_days || 'N/A'} dias</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Reatribuições IA</span>
                <span className="text-sm text-slate-900">{stats?.ai_reassignments || 0}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Distribution by Tenant */}
      {stats?.tenants_distribution && stats.tenants_distribution.length > 0 && (
        <Card className="bg-white border-slate-200">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-indigo-700" />
              Distribuição por Cliente
            </CardTitle>
            <CardDescription className="text-slate-500">
              Denúncias por tenant
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.tenants_distribution.map((tenant, index) => (
                <div key={tenant.id || tenant.slug || tenant.name || index} onClick={() => navigate('/super-admin/tenants')}
                  className="space-y-1 cursor-pointer hover:opacity-80 transition-opacity"
                  data-testid={`tenant-dist-${index}`}>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-900">{tenant.name}</span>
                    <span className="text-slate-500">{tenant.count} ({tenant.percentage}%)</span>
                  </div>
                  <Progress value={tenant.percentage} className="h-2 bg-slate-700" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="text-slate-900">Ações Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button 
              onClick={() => navigate('/super-admin/tenants')}
              className="bg-indigo-700 hover:bg-amber-600 text-black"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Criar Novo Cliente
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/super-admin/configuracoes')}
              className="border-slate-300 text-slate-600 hover:bg-slate-700"
            >
              <Shield className="w-4 h-4 mr-2" />
              Configurações Globais
            </Button>
          </div>
        </CardContent>
      </Card>

      <ReopenStatsCard />
    </div>
  );
};

export default SuperAdminDashboard;
